/* Wyszukujemy hosty komponentów w stronie, bez przekraczania Shadow DOM. */
export function findProgramComponent(tagName, programKey, root = document) {
  return [...root.querySelectorAll(tagName)].find(
    element => element.getAttribute('program-key') === programKey
  ) ?? null
}

export function getProgramTermsTarget(targetId, programKey, root = document) {
  return root.getElementById(targetId) ??
    findProgramComponent('ct-offer-terms', programKey, root)
}

let navigationRequest = 0

/* Czekamy na przeliczenie pozycji przez WebWave, ale nie blokujemy strony. */
export async function waitForStablePosition(target, isCancelled = () => false) {
  let lastTop = null
  let stableFrames = 0
  for (let frame = 0; frame < 90 && stableFrames < 6; frame++) {
    await new Promise(resolve => window.requestAnimationFrame(resolve))
    if (isCancelled() || !target.isConnected) return false
    const top = target.getBoundingClientRect().top + window.scrollY
    stableFrames = lastTop !== null && Math.abs(top - lastTop) < 1
      ? stableFrames + 1
      : 0
    lastTop = top
  }
  return !isCancelled()
}

/* Sprężyna tłumiona krytycznie: zmiana położenia celu nie powoduje skoku.
 * Pozycja i prędkość przechodzą płynnie między kolejnymi klatkami. */
export function stepScrollAnimation(position, velocity, target, seconds) {
  const omega = 12
  const displacement = position - target
  const coefficient = velocity + omega * displacement
  const decay = Math.exp(-omega * seconds)
  return {
    position: target + (displacement + coefficient * seconds) * decay,
    velocity: (velocity - omega * coefficient * seconds) * decay,
  }
}

export async function animateToProgramTerms(target, isCancelled) {
  let animatedPosition = window.scrollY
  let velocity = 0
  let previousTime = null
  let startedAt = null
  let stableFrames = 0
  for (;;) {
    const time = await new Promise(resolve => window.requestAnimationFrame(resolve))
    if (isCancelled() || !target.isConnected) return
    if (startedAt === null) startedAt = time
    if (time - startedAt > 5000) return
    const position = window.scrollY
    // Zachowujemy ułamki piksela, które scrollY może zaokrąglać. Większe
    // przesunięcie natywne (np. scroll anchoring) uwzględniamy od nowej pozycji.
    if (Math.abs(position - animatedPosition) > 2) animatedPosition = position
    const destination = Math.max(0, Math.min(
      target.getBoundingClientRect().top + position,
      Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
    ))
    const seconds = previousTime === null ? 1 / 60 :
      Math.min(.032, Math.max(.001, (time - previousTime) / 1000))
    previousTime = time
    const step = stepScrollAnimation(animatedPosition, velocity, destination, seconds)
    animatedPosition = step.position
    velocity = step.velocity
    window.scrollTo({ top: step.position, behavior: 'instant' })
    stableFrames = Math.abs(destination - window.scrollY) < 1 && Math.abs(velocity) < 3
      ? stableFrames + 1
      : 0
    if (stableFrames >= 6) return
  }
}

export async function scrollToProgramTerms(event, targetId, programKey) {
  const target = getProgramTermsTarget(targetId, programKey)
  if (!target) return
  event.preventDefault()
  const summary = findProgramComponent('ct-offer-summary', programKey)
  // Oferty bez nowego podsumowania zachowują pierwotną obsługę sticky CTA.
  if (!summary) {
    target.scrollIntoView({
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches
        ? 'auto'
        : 'smooth',
      block: 'start',
    })
    return
  }
  event.stopPropagation()
  const request = ++navigationRequest
  let cancelled = false
  const cancel = () => { cancelled = true }
  const isCancelled = () => cancelled || request !== navigationRequest
  const cancelEvents = ['wheel', 'touchstart', 'pointerdown', 'keydown']
  cancelEvents.forEach(name => window.addEventListener(name, cancel, { passive: true }))

  try {
    const terms = findProgramComponent('ct-offer-terms', programKey)
    await terms?.whenTermsReady?.()
    await document.fonts?.ready
    if (!await waitForStablePosition(target, isCancelled)) return
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      window.scrollTo({
        top: Math.max(0, target.getBoundingClientRect().top + window.scrollY),
        behavior: 'instant',
      })
    } else {
      await animateToProgramTerms(target, isCancelled)
    }
  } finally {
    cancelEvents.forEach(name => window.removeEventListener(name, cancel))
  }
}
